import { Component, Inject, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from "@angular/material/dialog";
import { RegionResponse } from "src/app/interfaces/responses/models";
import { RegionService } from "src/app/services/region.service";
import { UtilityService } from "src/app/services/utility.service";
import { ToastService } from "src/app/services/toast.service";
import { ConfirmDialogComponent } from 'src/app/shared/confirm-dialog/confirm-dialog.component';
import { AddUtilityRequestModel } from "src/app/interfaces/requests/models";
import { BaseComponent } from "src/app/shared/base.component";
import { takeUntil, switchMap } from "rxjs/operators";
import { of } from 'rxjs';

@Component({
  selector: "app-status-dialog",
  template: `
    <div class="custom-dialog">
      <button type="button" class="close-modal-btn" (click)="onCancel()">&times;</button>
      <h2 class="dialog-title">Select Status</h2>

      <div class="dropdown-container">
        <div class="dropdown-field" (click)="toggleDropdown()">
          <div class="status-badge" *ngIf="selectedStatus">
            <span [ngClass]="{'active-badge': selectedStatus === 'Active', 'retired-badge': selectedStatus === 'Retired'}">
              {{ selectedStatus }}
            </span>
          </div>
          <span *ngIf="!selectedStatus">Select Status</span>
          <div class="dropdown-arrow">▼</div>
        </div>

        <div class="dropdown-options" *ngIf="isDropdownOpen">
          <div *ngFor="let option of statusOptions" class="dropdown-option" (click)="selectOption(option)">
            <span [ngClass]="{'active-badge': option === 'Active', 'retired-badge': option === 'Retired'}">
              {{ option }}
            </span>
          </div>
        </div>
      </div>

      <div class="reason-field">
        <label for="statusReason">Please Enter a valid reason.</label>
        <textarea id="statusReason" [(ngModel)]="statusReason" class="reason-textarea" rows="3"></textarea>
      </div>

      <div class="dialog-actions">
        <button class="cancel-button" (click)="onCancel()">Cancel</button>
        <button class="save-button" [disabled]="!selectedStatus" (click)="onSave()">Save</button>
      </div>
    </div>
  `,
  styles: [`
    .custom-dialog { padding: 24px 64px; background-color: #204a56; color: white; border-radius: 24px; width: 100%; border: 1px solid #ffb600; position: relative; }
    .close-modal-btn { position: absolute; right: 20px; top: 20px; background: none; border: none; color: #557377; font-size: 32px; cursor: pointer; line-height: 1; transition: color 0.2s; }
    .close-modal-btn:hover { color: #ffffff; }
    .dialog-title { text-align: left; margin-top: 0; margin-bottom: 8px; font-size: 18px; font-weight: normal; }
    .dropdown-container { position: relative; margin-bottom: 16px; }
    .dropdown-field { display: flex; justify-content: space-between; align-items: center; background-color: white; border-radius: 8px; padding: 10px 16px; cursor: pointer; color: #204a56; }
    .dropdown-arrow { color: #204a56; }
    .dropdown-options { position: absolute; top: 100%; left: 0; width: 100%; background-color: white; border-radius: 8px; margin-top: 4px; z-index: 1000; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2); }
    .dropdown-option { padding: 10px 16px; cursor: pointer; color: #333; }
    .dropdown-option:hover { background-color: #f5f5f5; }
    .active-badge { background-color: #0cba5c; color: #204a56; padding: 4px 16px; border-radius: 16px; font-size: 16px; }
    .retired-badge { background-color: #9e9e9e; color: white; padding: 4px 16px; border-radius: 16px; font-size: 16px; }
    .reason-field { margin-bottom: 24px; }
    .reason-field label { display: block; margin-bottom: 8px; font-size: 18px; }
    .reason-textarea { width: 100%; border-radius: 4px; border: none; padding: 10px; resize: none; box-sizing: border-box; font-size: 18px; }
    .dialog-actions { display: flex; justify-content: space-between; }
    .cancel-button, .save-button { padding: 10px 0; border: none; border-radius: 4px; cursor: pointer; width: 48%; font-weight: bold; font-size: 20px; }
    .cancel-button { background-color: #c7c7c7; color: #204a56; }
    .save-button { background-color: #0cba5c; color: #204a56; }
    .save-button:disabled { opacity: 0.5; cursor: not-allowed; }
  `]
})
export class StatusDialogComponent {
  statusOptions = ["Active", "Retired"];
  selectedStatus = "";
  statusReason = "";
  isDropdownOpen = false;

  constructor(public dialogRef: MatDialogRef<StatusDialogComponent>) { }

  toggleDropdown(): void {
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  selectOption(option: string): void {
    this.selectedStatus = option;
    this.isDropdownOpen = false;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    this.dialogRef.close({ status: this.selectedStatus, reason: this.statusReason });
  }
}

@Component({
  selector: "app-utilities-add",
  templateUrl: "./utilities-add.component.html",
  styleUrls: ["./utilities-add.component.scss"],
})
export class UtilitiesAddComponent extends BaseComponent implements OnInit {
  utilityForm!: FormGroup;
  states: RegionResponse[] = [];
  logoPreviewUrl: string | ArrayBuffer | null = null;
  submitted = false;

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<UtilitiesAddComponent>,
    private dialog: MatDialog,
    private utilityService: UtilityService,
    private regionService: RegionService,
    private toastService: ToastService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    super();
    this.initForm();
  }

  private initForm(): void {
    this.utilityForm = this.fb.group({
      utilityName: ["", [Validators.required, Validators.pattern("^[a-zA-Z\\s]+$")]],
      description: ["", Validators.pattern("^[a-zA-Z\\s]*$")],
      email: ["", [Validators.required, Validators.email]],
      enterState: ["", Validators.required],
      phoneNumber: ["", [Validators.required, Validators.pattern("^[0-9]{10}$")]],
      enterLocality: ["", Validators.pattern("^[a-zA-Z0-9\\s]*$")],
      selectStatus: ["", Validators.required],
      statusReason: [""],
      enterPincode: ["", [Validators.required, Validators.pattern("^[0-9]{6}$")]],
      utilityLogo: [null],
    });
  }

  ngOnInit(): void {
    this.regionService.getStates()
      .pipe(takeUntil(this.destroy$))
      .subscribe(response => {
        this.states = response.items;
      });
  }

  onCancel(): void {
    if (this.utilityForm.dirty) {
      const confirmRef = this.dialog.open(ConfirmDialogComponent, {
        panelClass: 'custom-dialog-container',
        data: {
          title: 'Confirmation',
          message: 'You have unsaved changes. Are you sure you want to discard them?'
        }
      });

      confirmRef.afterClosed()
        .pipe(takeUntil(this.destroy$))
        .subscribe(result => {
          if (result) this.dialogRef.close();
        });
    } else {
      this.dialogRef.close();
    }
  }

  onSubmit(): void {
    this.submitted = true;
    if (this.utilityForm.invalid) {
      this.toastService.show("Please fill all required fields correctly.", "error");
      return;
    }

    const payload = this.buildPayload();
    this.utilityService.addUtility(payload)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(response => {
          const logoFile = this.utilityForm.value.utilityLogo;
          if (logoFile) {
            return this.utilityService.uploadUtilityLogo(response.id, logoFile).pipe(
              switchMap(() => of(response))
            );
          }
          return of(response);
        })
      )
      .subscribe({
        next: (response) => {
          this.toastService.show("Utility created successfully!");
          this.dialogRef.close(response);
        },
        error: (err) => this.handleError(err)
      });
  }

  private buildPayload(): AddUtilityRequestModel {
    const f = this.utilityForm.value;
    return {
      name: f.utilityName,
      description: f.description || '',
      state: f.enterState,
      phone_number: String(f.phoneNumber),
      pin_code: String(f.enterPincode),
      address: f.enterLocality || '',
      email: f.email,
    };
  }

  private handleError(error: any): void {
    console.error("[UtilityAdd] Error:", error);
    if (error.status === 500) {
      this.toastService.show("Internal Server error");
    } else if (error.error?.error_code === 'E019') {
      this.toastService.show("Phone number already exists.", "error");
    } else {
      this.toastService.show(error.error?.message || "An unexpected error occurred.", "error");
    }
  }

  onFileSelected(event: any): void {
    const file: File = event.target.files[0];
    if (file) {
      this.utilityForm.patchValue({ utilityLogo: file });
      const reader = new FileReader();
      reader.onload = () => { this.logoPreviewUrl = reader.result; };
      reader.readAsDataURL(file);
    }
  }

  openStatusDialog(): void {
    const statusRef = this.dialog.open(StatusDialogComponent, {
      width: "500px",
      panelClass: "custom-dialog-container",
      disableClose: true,
    });

    statusRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result) {
          this.utilityForm.patchValue({
            selectStatus: result.status,
            statusReason: result.reason,
          });
        }
      });
  }

  // Common form field getters
  get utilityName() { return this.utilityForm.get("utilityName"); }
  get description() { return this.utilityForm.get("description"); }
  get email() { return this.utilityForm.get("email"); }
  get enterState() { return this.utilityForm.get("enterState"); }
  get phoneNumber() { return this.utilityForm.get("phoneNumber"); }
  get enterLocality() { return this.utilityForm.get("enterLocality"); }
  get statusControl() { return this.utilityForm.get("selectStatus"); }
  get enterPincode() { return this.utilityForm.get("enterPincode"); }
  get utilityLogo() { return this.utilityForm.get("utilityLogo"); }
}
